import { PORTAL_TYPES } from '@qler-view/shared_lib/src';
import { httpRequest } from '../../shared/lib/utils/httpClient';
import type { SuccessResponse } from '../services/loginService';

export interface SessionFields {
  fieldLabel: string;
  fieldValue: string;
}
export interface SessionDetails {
  id: number;
  patientName: string;
  sessionFields: SessionFields[];
  additionalSessionFields: SessionFields[];
  facilityDetails: SessionFields[];
  sessionStatus: string;
  waitEndTime: string;
  pastSessionCount: number;
  sessionDocumentCount: number;
  createdOn: string;
  isExternalConsult: boolean | null;
  isWebZoom: boolean;
  isDesktopZoom: boolean;
  unreadMessageCount: number;
  isFacilityChanged: boolean;
}

export interface PatientDetailsType {
  dateOfBirth: string;
  partnerName: string;
  facilityName: string;
  patientMrn: string;
  sessionStatus: string;
  callBack: string;
  complaint: string | null;
  patientId: string;
  patientFirstName: string;
  patientLastName: string;
  waitTime: string | null;
  waitStartTime: string | null;
  waitEndTime: string | null;
  isWebZoom: boolean;
  isDesktopZoom: boolean;
  isExternalConsult: boolean | null;
}

export interface FacilityDetailsInterface {
  partnerName: string;
  facilityName: string;
  referringProvider: string;
  deviceSelection: string | null;
  epicContext: string | null;
  assignedTo: string | null;
  waitStartTime: string;
}

export interface MoreDetailsData {
  patientDetails: PatientDetailsType;
  facilityDetails: FacilityDetailsInterface;
  pastSessionCount: number;
  sessionDocumentCount: number;
}

export interface PastSessionDetails {
  pastSessionRecords: PastSession[];
}
export interface PastSession {
  consultId: number;
  diagnosis: string;
  recommendation: string;
  providerFirstName: string;
  providerLastName: string;
  sessionStatus: string;
  waitStartTime: string;
  providerName: string;
  createdOn: string;
}

interface DdlOption {
  id: number;
  name: string;
}

export interface CompleteDDLData {
  diagnosis: DdlOption[];
  recommendations: DdlOption[];
  emCodes: DdlOption[];
}

export interface CompleteConsult {
  billable: boolean;
  diagnosis: number[];
  otherDiagnosis: string;
  recommendation: number;
  emCodes: number;
}

export interface SessionDoc {
  fileName: string;
  fileSize: number;
  documentId: number;
  documentUrl: string;
  providerId: number;
  consultId: number;
  facilityId: number;
  patientMrn: number;
  uploadedBy: string;
  uploadedOn: string;
}

export interface DocType {
  files: File[] | null;
  consultId: number;
  providerId: number;
}
export interface DocumentId {
  documentId: number;
}

export interface CancelConsultMetadataResponse {
  cancelReasonList: DdlOption[];
  cancelTechIssueList: DdlOption[];
}

export interface CancelReasonParams {
  cancelReasonId: number;
  technicalDifficultiesId: number;
  otherReason: string;
}

export interface AssigneeChangeAlertResponse {
  assignedToAnotherProvider: boolean;
  onHold: boolean;
}

export const getConsultMoreDetails = async (
  userId: number,
  consultId: number
): Promise<SessionDetails> => {
  const response = await httpRequest<SessionDetails>({
    url: `/clinical/consult/${consultId}/more-details?providerId=${userId}`,
    method: 'GET',
    headers: {
      userId: userId.toString(),
    },
  });
  return response;
};

export const relinquishConsult = async (
  userId: number,
  consultId: number
): Promise<MoreDetailsData> => {
  const response = await httpRequest<MoreDetailsData>({
    url: `/clinical/consult/${consultId}/relinquish-consult?providerId=${userId}`,
    method: 'PUT',
  });
  return response;
};
export const pastSession = async (
  userId: number,
  consultId: number
): Promise<PastSessionDetails> => {
  const response = await httpRequest<PastSessionDetails>({
    url: `/integration/consult/${consultId}/past-session`,
    method: 'GET',
    headers: {
      portalType: PORTAL_TYPES.CLINICAL,
      userId: userId.toString(),
    },
    apiBaseType: 'INTEGRATION',
  });
  return response;
};

export const getCompleteDropdownData = async (): Promise<CompleteDDLData> => {
  const response = await httpRequest<CompleteDDLData>({
    url: `/integration/consult/billable/metadata`,
    method: 'GET',
    headers: {
      portalType: PORTAL_TYPES.CLINICAL,
    },
    apiBaseType: 'INTEGRATION',
  });
  return response;
};

export const submitCompleteForm = async (
  userId: number,
  consultId: number,
  complete: CompleteConsult
): Promise<CompleteConsult> => {
  const response = await httpRequest<CompleteConsult>({
    url: `/integration/consult/${consultId}/call-completed?providerId=${userId}`,
    method: 'PUT',
    data: complete,
    apiBaseType: 'INTEGRATION',
    headers: {
      portalType: PORTAL_TYPES.CLINICAL,
    },
  });
  return response;
};

export const getSessionDocument = async (
  consultId: number
): Promise<SessionDoc[]> => {
  const response = await httpRequest<SessionDoc[]>({
    url: `/integration/documents/consult/${consultId}`,
    apiBaseType: 'INTEGRATION',
    method: 'GET',
  });
  return response;
};
export const sessionDownloadDocument = async (
  docId: number
): Promise<string> => {
  const response = await httpRequest<string>({
    url: `/integration/documents/${docId}/download`,
    apiBaseType: 'INTEGRATION',
    method: 'GET',
  });
  return response;
};

export const uploadSessionDocument = async (
  data: DocType
): Promise<SuccessResponse> => {
  const formData = new FormData();
  if (data.files !== null && data.files.length > 0) {
    data.files.forEach((file) => {
      formData.append(`files`, file);
    });
    formData.append('consultId', `${data.consultId}`);
    formData.append('providerId', `${data.providerId}`);
  } else {
    formData.append('file', '');
    formData.append('consultId', `${data.consultId}`);
    formData.append('providerId', `${data.providerId}`);
  }

  const response = await httpRequest<SuccessResponse>({
    url: `/integration/documents/upload`,
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    apiBaseType: 'INTEGRATION',
    data: formData,
  });
  return response;
};

export const sessionDeleteDocument = async (
  docId: number
): Promise<SuccessResponse> => {
  const response = await httpRequest<SuccessResponse>({
    url: `/integration/documents/${docId}`,
    method: 'DELETE',
    apiBaseType: 'INTEGRATION',
  });
  return response;
};

export const submitCancelForm = async (
  consultId: number,
  data: CancelReasonParams
): Promise<CancelReasonParams> => {
  const response = await httpRequest<CancelReasonParams>({
    url: `/integration/consult/${consultId}/cancel`,
    method: 'PUT',
    data: data,
    headers: {
      userId: localStorage.getItem('userId') ?? '',
      portalType: PORTAL_TYPES.CLINICAL,
    },
    apiBaseType: 'INTEGRATION',
  });
  return response;
};

export const getCancelConsultMetadata =
  async (): Promise<CancelConsultMetadataResponse> => {
    const response = await httpRequest<CancelConsultMetadataResponse>({
      url: `integration/consult/cancel/metadata`,
      method: 'GET',
      headers: {
        portalType: PORTAL_TYPES.CLINICAL,
      },
      apiBaseType: 'INTEGRATION',
    });
    return response;
  };

export const assigneeChangeAlert = async (
  userId: number,
  consultId: number
) => {
  const response = await httpRequest<AssigneeChangeAlertResponse>({
    url: `/integration/${consultId}/assignee-change-alert?providerId=${userId}`,
    method: 'GET',
    headers: {
      portalType: PORTAL_TYPES.CLINICAL,
    },
    apiBaseType: 'INTEGRATION',
  });
  return response;
};
